import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const AdminCampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [config, setConfig] = useState<any>(null);
  const [leads, setLeads] = useState<any[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [escalations, setEscalations] = useState<any[]>([]);
  const [pricing, setPricing] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [welcomeMsg, setWelcomeMsg] = useState('');
  const [notifEmail, setNotifEmail] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!id) return;

      const [configRes, leadsRes, convsRes, escRes, pricingRes] = await Promise.all([
        supabase.from('campaign_configs').select('*').eq('id', id).single(),
        supabase.from('campaign_leads').select('*').eq('tenant_id', id).order('created_at', { ascending: false }).limit(100),
        supabase.from('conversations').select('*').eq('campaign_id', id).order('created_at', { ascending: false }).limit(50),
        supabase.from('escalations').select('*').eq('campaign_id', id).order('created_at', { ascending: false }).limit(50),
        supabase.from('pricing_lookup').select('*').limit(200),
      ]);

      if (configRes.data) {
        setConfig(configRes.data);
        setWelcomeMsg(configRes.data.welcome_message || '');
        setNotifEmail(configRes.data.notification_email || '');
        setIsActive(configRes.data.active);

        // Re-fetch leads and conversations by campaign slug
        const slug = configRes.data.campaign_slug;
        const tenantId = configRes.data.tenant_id;

        const [leadsRes2, convsRes2, escRes2, pricingRes2] = await Promise.all([
          supabase.from('campaign_leads').select('*').eq('campaign', slug).order('created_at', { ascending: false }).limit(200),
          supabase.from('conversations').select('*').eq('campaign_id', configRes.data.id).order('created_at', { ascending: false }).limit(100),
          supabase.from('escalations').select('*').eq('campaign_id', configRes.data.id).order('created_at', { ascending: false }).limit(100),
          supabase.from('pricing_lookup').select('*').eq('campaign', slug).order('age_band', { ascending: true }),
        ]);

        setLeads((leadsRes2.data as any[]) || []);
        setConversations((convsRes2.data as any[]) || []);
        setEscalations((escRes2.data as any[]) || []);
        setPricing((pricingRes2.data as any[]) || []);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const handleSaveConfig = async () => {
    if (!id) return;
    setSaving(true);
    const { error } = await supabase.from('campaign_configs').update({
      welcome_message: welcomeMsg,
      notification_email: notifEmail,
      active: isActive,
    }).eq('id', id);

    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Saved', description: 'Campaign config updated.' });
    }
    setSaving(false);
  };

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><LoadingSpinner /></div></AdminLayout>;
  }

  if (!config) {
    return <AdminLayout><p className="text-muted-foreground">Campaign not found.</p></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/admin/campaigns">
            <Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{config.campaign_name}</h1>
            <p className="text-muted-foreground text-sm">{config.campaign_slug} · {config.brand_name || 'No brand'}</p>
          </div>
          <Badge variant={config.active ? 'default' : 'secondary'} className="ml-auto">
            {config.active ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="conversations">Conversations ({conversations.length})</TabsTrigger>
            <TabsTrigger value="escalations">Escalations ({escalations.length})</TabsTrigger>
            <TabsTrigger value="config">Config</TabsTrigger>
            <TabsTrigger value="pricing">Pricing ({pricing.length})</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Total Leads</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{leads.length}</p></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Hot Leads</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold text-orange-500">{leads.filter((l: any) => l.lead_temperature === 'hot').length}</p></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Quotes Given</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{leads.filter((l: any) => l.quote_given).length}</p></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Handoffs</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{leads.filter((l: any) => l.handoff_requested).length}</p></CardContent></Card>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Conversations</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{conversations.length}</p></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Escalations</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{escalations.length}</p></CardContent></Card>
            </div>
          </TabsContent>

          {/* Leads */}
          <TabsContent value="leads">
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Name</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Email</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">Phone</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground">Score</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground">Temp</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground hidden sm:table-cell">Quote</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden lg:table-cell">Action</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden xl:table-cell">Source</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden xl:table-cell">Platform</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden xl:table-cell">Form ID</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground hidden lg:table-cell">Prefill Source</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground hidden lg:table-cell">Conv</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l: any) => (
                    <tr key={l.id} className="border-b hover:bg-muted/30">
                      <td className="px-3 py-2 font-medium">{l.lead_name || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{l.lead_email || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{l.lead_phone || '—'}</td>
                      <td className="px-3 py-2 text-center">{l.lead_score ?? '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant={l.lead_temperature === 'hot' ? 'destructive' : l.lead_temperature === 'warm' ? 'default' : 'secondary'} className="text-xs">
                          {l.lead_temperature || '—'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-center hidden sm:table-cell">
                        {l.quote_given ? `$${l.quoted_weekly_premium?.toFixed(2) || '?'}/wk` : '—'}
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground">{l.recommended_action || '—'}</td>
                      <td className="px-3 py-2 hidden xl:table-cell text-xs text-muted-foreground">{l.source_channel || '—'}</td>
                      <td className="px-3 py-2 hidden xl:table-cell text-xs text-muted-foreground">{l.source_platform || '—'}</td>
                      <td className="px-3 py-2 hidden xl:table-cell text-xs font-mono text-muted-foreground">{l.source_form_id || '—'}</td>
                       <td className="px-3 py-2 text-center hidden lg:table-cell">
                        {l.prefill_source ? (
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              l.prefill_source === 'token' ? 'border-green-500 text-green-700' :
                              l.prefill_source === 'query_params' ? 'border-blue-500 text-blue-700' :
                              l.prefill_source === 'email_lookup' ? 'border-amber-500 text-amber-700' :
                              ''
                            }`}
                          >
                            {l.prefill_source === 'token' ? '🔗 Token' :
                             l.prefill_source === 'query_params' ? '📋 Params' :
                             l.prefill_source === 'email_lookup' ? '📧 Lookup' :
                             l.prefill_source}
                          </Badge>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-center hidden lg:table-cell">
                        {l.conversation_id ? '✓' : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan={13} className="px-4 py-8 text-center text-muted-foreground">No leads yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Conversations */}
          <TabsContent value="conversations">
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Session</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Type</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground">Messages</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">Channel</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((c: any) => (
                    <tr key={c.id} className="border-b hover:bg-muted/30">
                      <td className="px-3 py-2 font-mono text-xs">{(c.session_id || c.id)?.slice(0, 12)}…</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{c.conversation_type || 'campaign'}</Badge></td>
                      <td className="px-3 py-2 text-center">{Array.isArray(c.messages) ? c.messages.length : '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{c.source_channel || '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {conversations.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No conversations yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Escalations */}
          <TabsContent value="escalations">
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Type</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Reason</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">Sent To</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {escalations.map((e: any) => (
                    <tr key={e.id} className="border-b hover:bg-muted/30">
                      <td className="px-3 py-2"><Badge variant="destructive" className="text-xs">{e.escalation_type || 'general'}</Badge></td>
                      <td className="px-3 py-2 text-muted-foreground">{e.reason || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{e.sent_to_email || '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {escalations.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No escalations.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Config */}
          <TabsContent value="config">
            <Card>
              <CardHeader><CardTitle>Campaign Configuration</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground">Welcome Message</label>
                  <Textarea value={welcomeMsg} onChange={e => setWelcomeMsg(e.target.value)} rows={3} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground">Notification Email</label>
                  <Input value={notifEmail} onChange={e => setNotifEmail(e.target.value)} className="mt-1" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} id="active-toggle" className="rounded" />
                  <label htmlFor="active-toggle" className="text-sm font-medium text-foreground">Active</label>
                </div>
                <Button onClick={handleSaveConfig} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing */}
          <TabsContent value="pricing">
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Age Band</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Gender</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Smoker</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Weekly Premium</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.map((p: any) => (
                    <tr key={p.id} className="border-b hover:bg-muted/30">
                      <td className="px-3 py-2">{p.age_band}</td>
                      <td className="px-3 py-2">{p.gender}</td>
                      <td className="px-3 py-2">{p.smoker_status}</td>
                      <td className="px-3 py-2 text-right font-medium">${p.weekly_premium?.toFixed(2)}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant={p.active ? 'default' : 'secondary'} className="text-xs">{p.active ? 'Yes' : 'No'}</Badge>
                      </td>
                    </tr>
                  ))}
                  {pricing.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No pricing rows.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminCampaignDetail;
